import {createStyles} from '@mantine/core';
import React from 'react';
import {ReactFlow, ReactFlowProvider} from 'reactflow';

import ReactFlowWrapper from 'processes/graph-builder/flowchart-viz/ReactFlowWrapper';
import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {BaseCondition} from 'studies/typed/shared/useExperimentDesignState';
import getTestProps from 'test/lib/cypress/getTestProps';
import DefaultBackground from 'ui-library/reactflow/backgrounds/DefaultBackground';
import HorizontalControls, {MIN_ZOOM, MAX_ZOOM} from 'ui-library/reactflow/controls/HorizontalControls';
import {REACT_FLOW_PRO_OPTIONS} from 'ui-library/reactflow/ReactFlowConstants';

import ProcedureRunDesignFlowchartColumnNode, {
  PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE,
} from './ProcedureRunDesignFlowchartColumnNode';
import ProcedureRunDesignFlowchartNode, {
  PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE,
} from './ProcedureRunDesignFlowchartNode';
import {useProcedureRunDesignFlowchartHover} from './useProcedureRunDesignFlowchartHover';
import {useProcedureRunDesignFlowchartLayout} from './useProcedureRunDesignFlowchartLayout';
import type {ProcedureRunDesignFlowchartLayoutOptions} from './useProcedureRunDesignFlowchartLayout';

const LAYOUT_OPTIONS: ProcedureRunDesignFlowchartLayoutOptions = {
  horizontalGap: 75,
  verticalGap: 32,
  startX: 24,
  startY: 24,
  columnHeaderBottomGap: 20,
};

const NODE_TYPES = {
  [PROCEDURE_RUN_DESIGN_FLOWCHART_NODE_TYPE]: ProcedureRunDesignFlowchartNode,
  [PROCEDURE_RUN_DESIGN_FLOWCHART_COLUMN_NODE_TYPE]: ProcedureRunDesignFlowchartColumnNode,
};

const STATIC_PROPS: Partial<React.ComponentProps<typeof ReactFlow>> = {
  nodesConnectable: false,
  nodesDraggable: false,
  panOnScroll: true,
  panOnDrag: true,
  zoomOnScroll: false,
  zoomOnPinch: true,
  defaultViewport: {x: 0, y: 0, zoom: 1},
  minZoom: MIN_ZOOM,
  maxZoom: MAX_ZOOM,
  proOptions: REACT_FLOW_PRO_OPTIONS,
};

interface ProcedureRunDesignFlowchartProps {
  conditions: Array<BaseCondition>;
  methods: Array<ExperimentMethod>;
  controlsShiftLeft?: number;
  onNodeClick?: () => void;
}

function ProcedureRunDesignFlowchartContent({
  conditions,
  methods,
  controlsShiftLeft,
  onNodeClick,
}: ProcedureRunDesignFlowchartProps): JSX.Element {
  const {classes} = useStyles();
  const {hoveredMethodId, onNodeMouseEnter, onNodeMouseLeave} = useProcedureRunDesignFlowchartHover();
  const {nodes, onNodesChange} = useProcedureRunDesignFlowchartLayout({
    conditions,
    methods,
    layoutOptions: LAYOUT_OPTIONS,
    methodHighlightState: {
      hoveredMethodId,
    },
  });

  return (
    <div
      className={classes.flowchartContainer}
      {...getTestProps({component: 'ProcedureRunDesignFlowchart', element: 'root'})}
    >
      <ReactFlow
        nodes={nodes}
        onNodesChange={onNodesChange}
        // TODO (BNCH-156156): Add edges
        edges={[]}
        nodeTypes={NODE_TYPES}
        {...STATIC_PROPS}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onNodeClick={onNodeClick}
      >
        <HorizontalControls shiftLeft={controlsShiftLeft ?? 0} />
        <DefaultBackground />
      </ReactFlow>
      {/* TODO (BNCH-153423): Add empty state for when there are no nodes */}
    </div>
  );
}

export default function ProcedureRunDesignFlowchart(props: ProcedureRunDesignFlowchartProps): JSX.Element {
  return (
    <ReactFlowProvider>
      <ReactFlowWrapper fullWidth={true}>
        <ProcedureRunDesignFlowchartContent {...props} />
      </ReactFlowWrapper>
    </ReactFlowProvider>
  );
}

const useStyles = createStyles(() => ({
  flowchartContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
}));
